import { ScaleLinear, scaleLinear, type Selection } from "d3";

import { Cartesian } from "@examspark/types";

const clamp = (min: number, max: number, value: number) =>
  Math.min(Math.max(value, min), max);

export const roundToGrid = (position: number, axis: D3Axis) => {
  if (axis.gridDistance) {
    return clamp(
      axis.min,
      axis.max,
      Math.round(position / axis.gridDistance) * axis.gridDistance,
    );
  } else {
    // if there is no gridDistance, we would have used the default ticks
    // in the graph, so round to those
    return axis.scale
      .ticks()
      .reduce((prev, curr) =>
        Math.abs(curr - position) < Math.abs(prev - position) ? curr : prev,
      );
  }
};

export const ticksForStep = (axis: D3Axis, step: number) => {
  const ticks: any[] = [];
  const domain = axis.scale.domain();
  const range = axis.scale.range();

  const min = Math.min(...domain);
  const max = Math.max(...domain);
  const start = Math.ceil(min / step) * step;
  const end = Math.floor(max / step) * step;
  for (let i = start; i <= end; i += step) {
    const x = axis.scale(i);
    // filter off 0 for style reasons
    if (x >= range[0] && x <= range[1] && i !== 0) {
      ticks.push(i);
    }
  }
  return ticks;
};

export type D3Axis = Cartesian.Axis & {
  scale: ScaleLinear<number, number>;
};

export type D3Context = {
  svg: Selection<SVGElement, unknown, null, undefined>;
  x: D3Axis;
  y: D3Axis;
};

export const calculateScales = (props: {
  width: number;
  height: number;
  x: Cartesian.Axis;
  y: Cartesian.Axis;
}) => {
  // HACK: Set default values that match current graph behaviors until we can
  // set these in  a migration. We WANT undefined/0 in tick/gridDistance to set
  // a nice default, but we don't want to break existing graphs in the meantime
  const x: D3Axis = {
    ...props.x,
    scale: scaleLinear()
      .domain([props.x.min, props.x.max])
      .range([0, props.width]),

    gridDistance: props.x.gridDistance || 1,
    tickDistance: props.x.tickDistance || 1,
  };

  const y: D3Axis = {
    ...props.y,
    scale: scaleLinear()
      .domain([props.y.max, props.y.min])
      .range([0, props.height]),

    gridDistance: props.y.gridDistance || 1,
    tickDistance: props.y.tickDistance || 1,
  };

  return { x, y };
};
